import { useQuery } from "@tanstack/react-query"
import { Link, useBlocker } from "@tanstack/react-router"
import { Background, Panel, ReactFlow, ReactFlowProvider } from "@xyflow/react"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { ExternalLink, Plus, Upload } from "lucide-react"
import { Loading } from "modules/shared/components/Loading"
import { RouteError } from "modules/shared/components/NotFound"
import { useMemo, useRef, useState } from "react"
import { useForm, useFormState } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { WorkflowResponseDataSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { useImportWorkflowMutation } from "../../hooks/useImportWorkflowMutation"
import { useWorkflowsCanvasSave } from "../../hooks/useWorkflowsCanvasSave"
import { useWorkflowsQuery } from "../../hooks/useWorkflowsQuery"
import { workflowsQuery } from "../../lib/workflowsQuery"
import { BuilderProvider } from "./BuilderContext"
import { useBuilderSaveContext } from "./hooks/useBuilderSave"
import { useWorkflowBuilder } from "./hooks/useWorkflowBuilder"
import { BuilderFormValues, isCanvasValid, workflowsToFlat } from "./lib/workflowsCanvas"
import { ActionNode } from "./nodes/ActionNode"
import { ConditionNode } from "./nodes/ConditionNode"
import { EmptyNode } from "./nodes/EmptyNode"
import { EventNode } from "./nodes/EventNode"
import { WorkflowBannerNode } from "./nodes/WorkflowBannerNode"
import { WorkflowGroupNode } from "./nodes/WorkflowGroupNode"
import { WorkflowToolbarNode } from "./nodes/WorkflowToolbarNode"
import { CampaignSheet } from "./sheets/CampaignSheet"
import { ZoomSlider } from "./ZoomSlider"

import "@xyflow/react/dist/style.css"
import "./flowStyles.css"

function Base({ globalCount }: { globalCount: number }) {
  const { t } = useTranslation()
  const { nodes, edges, setNodes, setEdges, form, onAddWorkflow, onImportWorkflow } =
    useWorkflowBuilder()
  const { flush } = useBuilderSaveContext()
  const [isSaving, setIsSaving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { isDirty } = useFormState({ control: form.control })

  const { mutate: importWorkflow } = useImportWorkflowMutation()

  async function onFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Reset so re-selecting the same file fires onChange again.
    event.target.value = ""
    if (!file) return

    // A modified or invalid file is rejected server-side via the global mutation error handler.
    // On success we load the preview onto the canvas as an unsaved draft — nothing is persisted yet.
    importWorkflow(
      { file: await file.text() },
      {
        onSuccess: ({ blueprint, warnings }) => {
          onImportWorkflow(blueprint)
          if (warnings.length > 0) toast.warning(t("settings.workflows.import.warning"))
          else toast.success(t("settings.workflows.import.success"))
        },
      },
    )
  }

  // Group boxes are part of the controlled node state (see BuilderContext), so `nodes` already
  // contains them.
  const isValid = isCanvasValid(form.watch("workflows") ?? [], form.watch("nodes") ?? [])

  const nodeTypes = useMemo(
    () => ({
      workflowGroup: WorkflowGroupNode,
      workflowBanner: WorkflowBannerNode,
      workflowToolbar: WorkflowToolbarNode,
      event: EventNode,
      condition: ConditionNode,
      action: ActionNode,
      empty: EmptyNode,
    }),
    [],
  )

  // With no workflows yet, show a single clickable empty-state card instead of a blank canvas.
  const displayNodes = useMemo(() => {
    if (nodes.length > 0) return nodes
    return [
      {
        id: "__empty__",
        type: "empty",
        position: { x: 0, y: 0 },
        data: {},
        draggable: false,
        selectable: false,
      },
    ]
  }, [nodes])

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => isDirty,
    disabled: !isDirty,
    withResolver: true,
  })

  async function onSave() {
    try {
      setIsSaving(true)
      await flush()
      toast.success(t("settings.workflows.saved"))
    } finally {
      setIsSaving(false)
    }
  }

  const saveIsEnabled = isDirty && !isSaving && isValid

  return (
    <>
      <div className="flex-1 min-h-0 overflow-hidden bg-primary/5 relative">
        <ReactFlow
          nodes={displayNodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={setNodes}
          onEdgesChange={setEdges}
          snapToGrid
          snapGrid={[20, 20]}
          proOptions={{ hideAttribution: true }}
          fitView
          fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
          zoomOnDoubleClick={false}
        >
          <Background className="h-full" />
          <ZoomSlider position="top-left" className="sm:flex hidden !m-4" hideSlider />
          <Panel position="top-right" className="!m-4 flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="lg">
                  <Plus className="size-4" />
                  <span>{t("settings.workflows.addWorkflow")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onAddWorkflow}>
                  <Plus className="size-4" />
                  <span>{t("settings.workflows.new")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                  <Upload className="size-4" />
                  <span>{t("settings.workflows.import.label")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <input
              ref={fileInputRef}
              type="file"
              accept=".permutflow"
              className="hidden"
              onChange={onFileSelected}
            />
            {globalCount > 0 && (
              <Button variant="outline" size="lg" asChild>
                <Link to="/settings/workflows">
                  <span>{t("settings.workflows.globalHint", { count: globalCount })}</span>
                  <ExternalLink className="size-4" />
                </Link>
              </Button>
            )}
            <Button variant="default" size="lg" disabled={!saveIsEnabled} onClick={onSave}>
              {t("shared.save")}
            </Button>
          </Panel>
          <CampaignSheet />
        </ReactFlow>
      </div>
      <Dialog open={status === "blocked"} onOpenChange={reset}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("shared.blocker.title")}</DialogTitle>
            <DialogDescription>{t("shared.blocker.description")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={reset} className="mr-auto">
              {t("shared.cancel")}
            </Button>
            <Button onClick={proceed} variant="destructive">
              {t("shared.leave")}
            </Button>
            <Button
              onClick={async () => {
                await onSave()
                proceed?.()
              }}
              disabled={isSaving || !isValid}
            >
              {t("shared.blocker.saveAndLeave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function CanvasInner({
  workflows,
  globalCount,
  campaignId,
}: {
  workflows: z.infer<typeof WorkflowResponseDataSchema>[]
  globalCount: number
  campaignId?: string
}) {
  const defaults = useMemo<BuilderFormValues>(() => workflowsToFlat(workflows), [workflows])
  const form = useForm<BuilderFormValues>({ defaultValues: defaults })
  const { flush, sync } = useWorkflowsCanvasSave(form, workflows, campaignId)

  return (
    <BuilderProvider
      container={{ id: campaignId ?? "global", name: null }}
      form={form}
      flush={flush}
      sync={sync}
      defaults={defaults}
    >
      <div className="flex flex-col flex-1 min-h-0 gap-0">
        <Base globalCount={globalCount} />
      </div>
    </BuilderProvider>
  )
}

// Multi-workflow canvas. With a campaignId it edits that campaign's workflows; without one it edits
// global workflows. Each workflow is a labelled box around its events + their trees.
export function WorkflowsCanvas({ campaignId }: { campaignId?: string }) {
  const { data: workflows, isPending, isError, error } = useWorkflowsQuery(campaignId)

  // Inside a campaign, surface how many enabled global workflows also run on its conversations.
  const { data: globalWorkflows } = useQuery({
    ...workflowsQuery(undefined),
    enabled: !!campaignId,
  })
  const globalCount = campaignId ? (globalWorkflows ?? []).filter((w) => w.isEnabled).length : 0

  if (isPending) return <Loading />
  if (isError || !workflows) return <RouteError showPage={false} error={error} />

  return (
    <ReactFlowProvider key={workflows.map((workflow) => workflow.id).join(",")}>
      <CanvasInner workflows={workflows} globalCount={globalCount} campaignId={campaignId} />
    </ReactFlowProvider>
  )
}
